import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar,
  Fade,
} from "@material-ui/core";

import { Button } from "../Wrappers";
import {
  getGroupMembers,
  addGroupMember,
  removeGroupMember,
  setUserPassword,
} from "../../context/GroupContext";
import { useTranslation } from "../../localization";

// Admin dialog to manage a group's users: list members, add a new username, and
// set any member's login password. Both the "add user" and "change password"
// flows funnel through a single inner prompt that asks for one string.
export default function ManageUsersDialog({ open, onClose, group }) {
  var t = useTranslation();

  var [members, setMembers] = useState([]);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState(null);
  var [toast, setToast] = useState("");
  // Inner prompt, or null when closed. "add"/"password" ask for one string;
  // "remove" is a confirmation with no text input:
  //   { mode: "add" | "password" | "remove", member?, value, loading, error }
  var [prompt, setPrompt] = useState(null);

  var loadMembers = useCallback(() => {
    setLoading(true);
    setError(null);
    getGroupMembers(group).then(
      list => {
        setMembers(list);
        setLoading(false);
      },
      err => {
        console.debug(err);
        setError(err.message || t.admin.loadMembersError);
        setLoading(false);
      },
    );
  }, [group, t.admin.loadMembersError]);

  useEffect(() => {
    if (open && group) {
      loadMembers();
    }
  }, [open, group, loadMembers]);

  function handleClose() {
    if (prompt && prompt.loading) {
      return;
    }
    setPrompt(null);
    onClose();
  }

  function openAddPrompt() {
    setPrompt({ mode: "add", value: "", loading: false, error: null });
  }

  function openPasswordPrompt(member) {
    setPrompt({ mode: "password", member, value: "", loading: false, error: null });
  }

  function openRemovePrompt(member) {
    setPrompt({ mode: "remove", member, value: "", loading: false, error: null });
  }

  function promptErrorFallback(mode) {
    if (mode === "add") {
      return t.admin.couldNotAddUser;
    }
    if (mode === "remove") {
      return t.admin.couldNotRemoveMember;
    }
    return t.admin.couldNotSetPassword;
  }

  async function handlePromptSubmit() {
    // "remove" is a confirmation and has no value to validate; the others
    // require non-empty input.
    if (!prompt || (prompt.mode !== "remove" && !prompt.value.trim())) {
      return;
    }
    var current = prompt;
    setPrompt({ ...current, loading: true, error: null });
    try {
      if (current.mode === "add") {
        await addGroupMember(group, current.value.trim());
        setPrompt(null);
        setToast(t.admin.userAdded);
        loadMembers();
      } else if (current.mode === "remove") {
        await removeGroupMember(group, current.member);
        setPrompt(null);
        setToast(t.admin.memberRemoved);
        loadMembers();
      } else {
        await setUserPassword(current.member, current.value);
        setPrompt(null);
        setToast(t.admin.passwordSet);
      }
    } catch (err) {
      console.debug(err);
      setPrompt({
        ...current,
        loading: false,
        error: err.message || promptErrorFallback(current.mode),
      });
    }
  }

  function handlePromptKeypress(e) {
    if (e.charCode === 13) {
      handlePromptSubmit();
    }
  }

  var promptTitle = !prompt
    ? ""
    : prompt.mode === "add"
    ? t.admin.addUserTitle
    : prompt.mode === "remove"
    ? t.admin.removeMemberButton
    : t.admin.changePasswordTitle(prompt.member);
  var promptLabel = !prompt
    ? ""
    : prompt.mode === "add"
    ? t.admin.addUserLabel
    : t.admin.passwordLabel;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
        <DialogTitle>{t.admin.usersTitle}</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 16 }}
          >
            {t.admin.usersDescription}
          </Typography>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <CircularProgress size={24} />
            </div>
          ) : error ? (
            <Typography color="secondary">{error}</Typography>
          ) : members.length === 0 ? (
            <Typography color="textSecondary">{t.admin.noMembers}</Typography>
          ) : (
            <List dense>
              {members.map(member => (
                <ListItem key={member} disableGutters divider>
                  <ListItemText primary={member} />
                  <ListItemSecondaryAction>
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => openPasswordPrompt(member)}
                    >
                      {t.admin.changePasswordButton}
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={() => openRemovePrompt(member)}
                    >
                      {t.admin.removeMemberButton}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            {t.common.close}
          </Button>
          <Button
            onClick={openAddPrompt}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {t.admin.addUserButton}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(prompt)}
        onClose={() => prompt && !prompt.loading && setPrompt(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{promptTitle}</DialogTitle>
        <DialogContent>
          <Fade in={Boolean(prompt && prompt.error)}>
            <Typography color="secondary" style={{ marginBottom: 8 }}>
              {prompt && prompt.error}
            </Typography>
          </Fade>
          {prompt && prompt.mode === "remove" ? (
            <Typography>
              {t.admin.removeMemberConfirm(prompt.member)}
            </Typography>
          ) : (
            <TextField
              autoFocus
              value={prompt ? prompt.value : ""}
              onChange={e => setPrompt(p => ({ ...p, value: e.target.value }))}
              onKeyPress={handlePromptKeypress}
              margin="normal"
              label={promptLabel}
              type="text"
              fullWidth
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPrompt(null)}
            color="secondary"
            disabled={prompt && prompt.loading}
          >
            {t.common.cancel}
          </Button>
          <Button
            onClick={handlePromptSubmit}
            variant="contained"
            color={prompt && prompt.mode === "remove" ? "secondary" : "primary"}
            disabled={
              !prompt ||
              prompt.loading ||
              (prompt.mode !== "remove" && !prompt.value.trim())
            }
          >
            {prompt && prompt.loading ? (
              <CircularProgress size={20} />
            ) : prompt && prompt.mode === "remove" ? (
              t.admin.removeMemberButton
            ) : (
              t.common.save
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}
